import { useEffect, useState } from "react";
import { ErrorNotice, useAction } from "../components/ui";
import { api, apiUrl, type ClientShortlist as Data, type ShortlistDecision } from "../lib/api";
import { displaySkill, formatSalary } from "../lib/format";
import { useTitle } from "../lib/title";
import { DECISION_LABEL } from "./JobShortlists";

const pad = (n: number) => String(n).padStart(2, "0");
const longDay = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long" });

/**
 * The page a hiring manager opens from the recruiter's link: no login. The token is in the
 * address after #, so it's never sent to a server except in our own API calls.
 */
export function ClientShortlist() {
  const token = window.location.hash.slice(1);
  const [data, setData] = useState<Data | null>(null);
  const load = useAction();
  useTitle(data ? `Shortlist: ${data.job.title}` : "Shortlist");

  useEffect(() => {
    if (!token) return;
    void load.run(async () => setData(await api.clientShortlist(token)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!data) {
    return (
      <main className="page client-shortlist">
        <h1 className="display">Shortlist</h1>
        {!token ? (
          <p className="lede">This link is incomplete. Open it again from the recruiter's email.</p>
        ) : load.error ? (
          <p className="lede">{load.error}</p>
        ) : (
          <p className="label">Loading…</p>
        )}
      </main>
    );
  }

  const answered = data.candidates.filter((c) => c.decision).length;

  return (
    <main className="page client-shortlist">
      <header className="page-head">
        <p className="label">
          {data.agency} · shortlist for {data.company}
        </p>
        <h1 className="display">{data.job.title}</h1>
        {data.message && <p className="lede">{data.message}</p>}
        <p className="secondary">
          From {data.recruiter.name} ·{" "}
          <a href={`mailto:${data.recruiter.email}?subject=${encodeURIComponent(`Re: ${data.job.title}`)}`}>{data.recruiter.email}</a> · this link works
          until {longDay.format(new Date(data.expiresAt))}
        </p>
      </header>

      <section className="section" aria-labelledby="people-h">
        <div className="section-head">
          <h2 id="people-h" className="label">
            {data.candidates.length} candidate{data.candidates.length === 1 ? "" : "s"}
          </h2>
          <span className="label figures">
            {answered} of {data.candidates.length} answered
          </span>
        </div>
        {data.candidates.length === 0 && <p className="empty-line">No one on this shortlist is available any more.</p>}
        <ol className="index client-people">
          {data.candidates.map((c, i) => (
            <Person key={c.id} n={i + 1} token={token} person={c} onAnswered={(decision, feedback) =>
              setData((d) => d && { ...d, candidates: d.candidates.map((x) => (x.id === c.id ? { ...x, decision, feedback } : x)) })
            } />
          ))}
        </ol>
      </section>

      {data.job.description && (
        <section className="section" aria-labelledby="jd-h">
          <details className="earlier">
            <summary id="jd-h" className="label">
              The job description
            </summary>
            <p className="prewrap">{data.job.description}</p>
          </details>
        </section>
      )}

      <p className="hint fine-print">
        These candidates' details are shared with you in confidence for this role only. Please don't forward this link or the CVs.
      </p>
    </main>
  );
}

function Person({
  n,
  token,
  person: c,
  onAnswered,
}: {
  n: number;
  token: string;
  person: Data["candidates"][number];
  onAnswered: (decision: ShortlistDecision, feedback: string) => void;
}) {
  const [choice, setChoice] = useState<ShortlistDecision | null>(c.decision);
  const [feedback, setFeedback] = useState(c.feedback);
  const [editing, setEditing] = useState(!c.decision);
  const { pending, error, run } = useAction();

  const facts = [
    c.currentTitle,
    c.location,
    c.noticePeriod && `notice ${c.noticePeriod}`,
    c.salaryExpectation && `looking for ${formatSalary(c.salaryExpectation, null, c.currency).replace("from ", "")}`,
  ].filter(Boolean);

  return (
    <li>
      <span className="num" aria-hidden>
        {pad(n)}
      </span>
      <span className="main">
        <span className="primary person-name">{c.fullName}</span>
        {facts.length > 0 && <span className="secondary">{facts.join(" · ")}</span>}
        {c.pitch && <span className="pitch prewrap">{c.pitch}</span>}
        {c.skills.length > 0 && (
          <span className="chips small">
            {c.skills.slice(0, 10).map((s) => (
              <span key={s} className="chip">
                {displaySkill(s)}
              </span>
            ))}
          </span>
        )}
        {c.hasCv && (
          <a className="link-btn cv-link" href={apiUrl(`/client/shortlist/cv?token=${encodeURIComponent(token)}&item=${c.id}`)} target="_blank" rel="noopener">
            Open CV
          </a>
        )}

        {editing ? (
          <form
            className="answer-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!choice) return;
              void run(async () => {
                await api.clientDecide(token, c.id, choice, feedback.trim());
                onAnswered(choice, feedback.trim());
                setEditing(false);
              });
            }}
          >
            <fieldset className="segmented">
              <legend className="label">Your answer</legend>
              {(["interview", "maybe", "no"] as const).map((d) => (
                <label key={d}>
                  <input type="radio" name={`answer-${c.id}`} checked={choice === d} onChange={() => setChoice(d)} />
                  <span>{DECISION_LABEL[d]}</span>
                </label>
              ))}
            </fieldset>
            <label className="field">
              <span className="label">Anything to add (optional)</span>
              <textarea
                rows={2}
                maxLength={2000}
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder={choice === "no" ? "What's missing? It helps find the right person." : "e.g. free Tuesday or Thursday afternoon"}
              />
            </label>
            <ErrorNotice error={error} />
            <div className="button-row">
              <button className="btn" disabled={!choice || pending}>
                {pending ? "Sending…" : "Send answer"}
              </button>
            </div>
          </form>
        ) : (
          <span className="answered">
            You said <strong>{DECISION_LABEL[c.decision!]}</strong>
            {c.feedback && <> · “{c.feedback}”</>}{" "}
            <button type="button" className="link-btn" onClick={() => setEditing(true)}>
              Change
            </button>
          </span>
        )}
      </span>
    </li>
  );
}
